import { safeReturnPath } from "@esi/shared";
import { type AnchorHTMLAttributes, useSyncExternalStore } from "react";
import { onRouteChange } from "./shell/build-refresh.ts";

/**
 * Minimal history router for the SPA's routes. CloudFront answers unknown paths with
 * `index.html`, so deep links and reloads land here; `parseRoute` maps every other path to
 * `notFound`.
 */
/** The tabs of a project's home page (Settings is its own route); `home` is Overview. */
export type ProjectTab = "home" | "conversations" | "files" | "wiki" | "memory" | "tasks";
export type WikiTab = "pages" | "changes" | "declassifications" | "activity";
/** The Changes inbox's views (wiki-collaboration-ux): `?tab=changes&view=`. */
export type InboxView = "to-decide" | "mine" | "following" | "closed";
export const INBOX_VIEWS: readonly InboxView[] = ["to-decide", "mine", "following", "closed"];
export type BackofficeSection =
  | "users"
  | "entities"
  | "settings"
  | "directory"
  | "residency"
  | "models"
  | "reports"
  | "projects"
  | "audit"
  | "plan"
  | "memory"
  | "branding"
  | "signin"
  | "support";
export type ReportTab = "usage" | "quality" | "cost" | "health";
/** A group of the backoffice settings, each on its own page (`/backoffice/settings/<group>`). */
export const SETTINGS_GROUPS = [
  "general",
  "security",
  "clients",
  "models",
  "knowledge",
  "limits",
  "integrations",
] as const;
export type SettingsGroup = (typeof SETTINGS_GROUPS)[number];
/** A group of a project's settings, each on its own page (`/projects/:id/settings/<group>`). */
export const PROJECT_SETTINGS_GROUPS = [
  "general",
  "members",
  "assistant",
  "knowledge",
  "web",
  "danger",
] as const;
export type ProjectSettingsGroup = (typeof PROJECT_SETTINGS_GROUPS)[number];

export type Route =
  | { name: "home" }
  | { name: "projects" }
  /**
   * A project's home page, on one tab: Overview at the bare `/projects/:id`, then Wiki (the
   * `wiki` route's `/wiki`), `/conversations`, `/files` (Sources), `/tasks` and `/memory`.
   */
  | { name: "projectHome"; projectId: string; tab: ProjectTab }
  /** One human task inside its project. */
  | { name: "projectTask"; projectId: string; taskId: string }
  /** A conversation of a project, or a new one (`conversationId` "new"), with its knowledge. */
  | { name: "projectConversation"; projectId: string; conversationId: string }
  /** A conversation by ID: outside any project, or redirected to its project URL. */
  | { name: "conversation"; conversationId: string }
  /** A former URL (`?conversation=`, `?tab=`), replaced by its new one for one release. */
  | { name: "redirect"; to: string }
  /** A project's settings on one group; without one, the page opens a group in place. */
  | { name: "projectSettings"; projectId: string; group?: ProjectSettingsGroup }
  | { name: "wiki"; projectId: string; pageId: string | null; tab: WikiTab; view?: InboxView }
  /** The page editor: a new page (in `folderId`, null: the root) or a change to `pageId`. */
  | { name: "wikiNew"; projectId: string; folderId: string | null }
  | { name: "wikiEdit"; projectId: string; pageId: string }
  /** A legacy draft link, replaced by its change set's review once resolved. */
  | { name: "wikiDraft"; projectId: string; draftId: string }
  | { name: "wikiChange"; projectId: string; changeSetId: string }
  | { name: "wikiHistory"; projectId: string; pageId: string }
  /** Two versions of a page, oldest first; null versions: an invalid link (not found). */
  | {
      name: "wikiCompare";
      projectId: string;
      pageId: string;
      from: number | null;
      to: number | null;
    }
  | { name: "conversations" }
  | { name: "tasks" }
  | { name: "knowledge" }
  /**
   * `projectId`: one governed project (`/backoffice/projects/<id>`); `settingsGroup`: one
   * settings page (`/backoffice/settings/<group>`; without it, the settings redirect to the first).
   */
  | {
      name: "backoffice";
      section: BackofficeSection | null;
      projectId?: string;
      settingsGroup?: SettingsGroup;
    }
  /** The instance console (operators only; anyone else gets not-found). */
  | { name: "console"; view: ConsoleView }
  /** Account settings, or one of its sub-pages ("My feedback", in-app-feedback). */
  | { name: "account"; view?: "feedback" }
  /** Standalone pages of the links ESI emails (outside the shell, signed in or not). */
  | { name: "invitation" }
  | { name: "resetPassword" }
  /** ESI's sign-in page for a flow that comes back to `returnTo` (a native client's sign-in). */
  | { name: "signIn"; returnTo: string; reauth: boolean }
  /** A native client's consent screen (authorization server interaction `uid`). */
  | { name: "connect"; uid: string }
  /** ESI's device verification page; the code from `verification_uri_complete`, if any. */
  | { name: "device"; userCode: string | null }
  | { name: "notFound" };

/** A screen of the instance console. */
export type ConsoleView =
  | { name: "tenants" }
  /** One tenant, one page per tab (`/tenants/<id>/<tab>`; the bare URL is Overview). */
  | { name: "tenant"; tenantId: string; tab: TenantTab }
  | { name: "plans" }
  /** Model connections (model-connections-cross-account): registry, route order, allowlist. */
  | { name: "modelConnections" }
  /** The instance policies, one page per settings group (`/policies/<group>`). */
  | { name: "policies"; group?: SettingsGroup }
  | { name: "support" }
  /** A tenant's backoffice, read-only, under one of the operator's support grants. */
  | {
      name: "supportGrant";
      grantId: string;
      section: BackofficeSection | null;
      projectId?: string;
      settingsGroup?: SettingsGroup;
    }
  | { name: "reports" }
  /** Feedback users sent (in-app-feedback): the inbox, or one report. */
  | { name: "feedback"; reportId?: string }
  /** Feedback triage (in-app-feedback slice 3): the groups, or one group. */
  | { name: "feedbackGroups"; groupId?: string }
  /** Operators: their sessions now, operator management later (#238). */
  | { name: "operators"; tab: OperatorsTab }
  /** The signed-in operator's own profile: identity, authenticator and passkeys (#238). */
  | { name: "profile" };

export const TENANT_TABS = ["overview", "admins", "sign-in", "limits", "lifecycle"] as const;
export type TenantTab = (typeof TENANT_TABS)[number];
export const OPERATORS_TABS = ["sessions", "members", "audit"] as const;
export type OperatorsTab = (typeof OPERATORS_TABS)[number];

/** The sidebar area a route belongs to (marked current). */
export type Area = "home" | "projects" | "conversations" | "tasks" | "knowledge" | "backoffice";

const UUID = "[0-9a-f-]{36}";
/** `/connect/<uid>`: the provider's interaction UIDs are base64url. */
const CONNECT_PATH = /^\/connect\/([A-Za-z0-9_-]{8,128})$/;
const PROJECT_PATH = new RegExp(
  `^/projects/(${UUID})(?:/(conversations|files|memory|tasks))?$`,
  "i",
);
const PROJECT_TASK_PATH = new RegExp(`^/projects/(${UUID})/tasks/(${UUID})$`, "i");
const PROJECT_CONVERSATION_PATH = new RegExp(`^/projects/(${UUID})/c/(${UUID}|new)$`, "i");
const CONVERSATION_PATH = new RegExp(`^/c/(${UUID})$`, "i");
const SETTINGS_PATH = new RegExp(
  `^/projects/(${UUID})/settings(?:/(${PROJECT_SETTINGS_GROUPS.join("|")}))?$`,
  "i",
);
const WIKI_PATH = new RegExp(`^/projects/(${UUID})/wiki(?:/(${UUID}))?$`, "i");
const DRAFT_PATH = new RegExp(`^/projects/(${UUID})/wiki/drafts(?:/([^/]+)(/edit)?)?(/.*)?$`, "i");
const NEW_PAGE_PATH = new RegExp(`^/projects/(${UUID})/wiki/new$`, "i");
const EDIT_PAGE_PATH = new RegExp(`^/projects/(${UUID})/wiki/(${UUID})/edit$`, "i");
const UUID_VALUE = new RegExp(`^${UUID}$`, "i");
const CHANGE_PATH = new RegExp(`^/projects/(${UUID})/wiki/changes/(${UUID})$`, "i");
const HISTORY_PATH = new RegExp(`^/projects/(${UUID})/wiki/(${UUID})/history$`, "i");
const COMPARE_PATH = new RegExp(`^/projects/(${UUID})/wiki/(${UUID})/compare$`, "i");
/** A positive version number from a query parameter, else null. */
const versionParam = (value: string | null) =>
  value && /^[1-9]\d{0,8}$/.test(value) ? Number(value) : null;
const WIKI_TABS: readonly WikiTab[] = ["pages", "changes", "declassifications", "activity"];
const SECTIONS =
  "users|entities|settings|directory|residency|models|reports|projects|audit|plan|memory|branding|signin|support";
const BACKOFFICE_PATH = new RegExp(`^/backoffice(?:/(${SECTIONS}))?$`);
const GROUPS = SETTINGS_GROUPS.join("|");
const SETTINGS_GROUP_PATH = new RegExp(`^/backoffice/settings/(${GROUPS})$`);
const GOVERNED_PROJECT_PATH = new RegExp(`^/backoffice/projects/(${UUID})$`, "i");
const CONSOLE_POLICIES_GROUP_PATH = new RegExp(`^/policies/(${GROUPS})$`);
// The first tab of each is its bare URL only: `/tenants/<id>/overview` and
// `/operators/sessions` redirect there instead of being second addresses.
const CONSOLE_TENANT_PATH = new RegExp(
  `^/tenants/(${UUID})(?:/(${TENANT_TABS.slice(1).join("|")}))?$`,
  "i",
);
const CONSOLE_TENANT_FIRST_TAB_PATH = new RegExp(`^/tenants/(${UUID})/${TENANT_TABS[0]}$`, "i");
const CONSOLE_OPERATORS_PATH = new RegExp(
  `^/operators(?:/(${OPERATORS_TABS.slice(1).join("|")}))?$`,
);
const CONSOLE_FEEDBACK_REPORT_PATH = new RegExp(`^/feedback/(${UUID})$`, "i");
const CONSOLE_FEEDBACK_GROUP_PATH = new RegExp(`^/feedback/groups/(${UUID})$`, "i");
const CONSOLE_GRANT_PATH = new RegExp(`^/support/(${UUID})(?:/(${SECTIONS}))?$`, "i");
const CONSOLE_GRANT_SETTINGS_PATH = new RegExp(`^/support/(${UUID})/settings/(${GROUPS})$`, "i");
const CONSOLE_GRANT_PROJECT_PATH = new RegExp(`^/support/(${UUID})/projects/(${UUID})$`, "i");

export function parseRoute(pathname: string, search = ""): Route {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  switch (path) {
    case "/":
      return { name: "home" };
    case "/projects":
      return { name: "projects" };
    case "/conversations":
      return { name: "conversations" };
    case "/tasks":
      return { name: "tasks" };
    case "/knowledge":
      return { name: "knowledge" };
    case "/account":
      return { name: "account" };
    case "/account/feedback":
      return { name: "account", view: "feedback" };
    case "/invitation":
      return { name: "invitation" };
    case "/reset-password":
      return { name: "resetPassword" };
    case "/device":
      return {
        name: "device",
        userCode: new URLSearchParams(search).get("user_code")?.slice(0, 32) || null,
      };
    case "/signin": {
      const params = new URLSearchParams(search);
      return {
        name: "signIn",
        returnTo: safeReturnPath(params.get("returnTo")),
        reauth: params.get("reauth") === "1",
      };
    }
  }
  const connect = CONNECT_PATH.exec(path);
  if (connect?.[1]) {
    return { name: "connect", uid: connect[1] };
  }
  // A console address on the tenant site (the console has its own website): a plain
  // not-found, rendered by `App`.
  if (path === LEGACY_CONSOLE_PREFIX || path.startsWith(`${LEGACY_CONSOLE_PREFIX}/`)) {
    return { name: "console", view: { name: "tenants" } };
  }
  const backoffice = BACKOFFICE_PATH.exec(path);
  if (backoffice) {
    return { name: "backoffice", section: (backoffice[1] as BackofficeSection) ?? null };
  }
  const settingsGroup = SETTINGS_GROUP_PATH.exec(path);
  if (settingsGroup?.[1]) {
    return {
      name: "backoffice",
      section: "settings",
      settingsGroup: settingsGroup[1] as SettingsGroup,
    };
  }
  const governed = GOVERNED_PROJECT_PATH.exec(path);
  if (governed?.[1]) {
    return { name: "backoffice", section: "projects", projectId: governed[1].toLowerCase() };
  }
  const params = new URLSearchParams(search);
  const settings = SETTINGS_PATH.exec(path);
  if (settings?.[1]) {
    const group = settings[2]?.toLowerCase() as ProjectSettingsGroup | undefined;
    return group
      ? { name: "projectSettings", projectId: settings[1], group }
      : { name: "projectSettings", projectId: settings[1] };
  }
  const change = CHANGE_PATH.exec(path);
  if (change?.[1] && change[2]) {
    return { name: "wikiChange", projectId: change[1], changeSetId: change[2].toLowerCase() };
  }
  const history = HISTORY_PATH.exec(path);
  if (history?.[1] && history[2]) {
    return { name: "wikiHistory", projectId: history[1], pageId: history[2].toLowerCase() };
  }
  const compare = COMPARE_PATH.exec(path);
  if (compare?.[1] && compare[2]) {
    const a = versionParam(params.get("from"));
    const b = versionParam(params.get("to"));
    const valid = a !== null && b !== null;
    return {
      name: "wikiCompare",
      projectId: compare[1],
      pageId: compare[2].toLowerCase(),
      from: valid ? Math.min(a, b) : null,
      to: valid ? Math.max(a, b) : null,
    };
  }
  const newPage = NEW_PAGE_PATH.exec(path);
  if (newPage?.[1]) {
    const folder = params.get("folder");
    return {
      name: "wikiNew",
      projectId: newPage[1],
      folderId: folder && UUID_VALUE.test(folder) ? folder.toLowerCase() : null,
    };
  }
  const editPage = EDIT_PAGE_PATH.exec(path);
  if (editPage?.[1] && editPage[2]) {
    return { name: "wikiEdit", projectId: editPage[1], pageId: editPage[2].toLowerCase() };
  }
  // Legacy draft URLs, redirected for good: a new draft opens the page editor, a draft its
  // change set (resolved by LegacyDraftLink), anything else the wiki's review queue.
  const draft = DRAFT_PATH.exec(path);
  if (draft?.[1]) {
    const id = draft[4] ? "" : (draft[2] ?? "");
    if (id.toLowerCase() === "new" && !draft[3]) {
      const pageId = params.get("page");
      return {
        name: "redirect",
        to:
          pageId && UUID_VALUE.test(pageId)
            ? editPagePath(draft[1], pageId.toLowerCase())
            : newPagePath(draft[1]),
      };
    }
    if (UUID_VALUE.test(id)) {
      return { name: "wikiDraft", projectId: draft[1], draftId: id.toLowerCase() };
    }
    return { name: "redirect", to: wikiChangesPath(draft[1]) };
  }
  const wiki = WIKI_PATH.exec(path);
  if (wiki?.[1]) {
    const tab = params.get("tab");
    // The former Review queue is the Changes inbox's To decide (replacing the history entry).
    if (!wiki[2] && tab === "review") {
      return { name: "redirect", to: wikiChangesPath(wiki[1]) };
    }
    const view = params.get("view");
    return {
      name: "wiki",
      projectId: wiki[1],
      pageId: wiki[2] ?? null,
      tab: !wiki[2] && WIKI_TABS.includes(tab as WikiTab) ? (tab as WikiTab) : "pages",
      ...(!wiki[2] &&
        tab === "changes" && {
          view: INBOX_VIEWS.includes(view as InboxView) ? (view as InboxView) : "to-decide",
        }),
    };
  }
  const conversation = CONVERSATION_PATH.exec(path);
  if (conversation?.[1]) {
    return { name: "conversation", conversationId: conversation[1].toLowerCase() };
  }
  const projectConversation = PROJECT_CONVERSATION_PATH.exec(path);
  if (projectConversation?.[1] && projectConversation[2]) {
    return {
      name: "projectConversation",
      projectId: projectConversation[1],
      conversationId: projectConversation[2].toLowerCase(),
    };
  }
  const task = PROJECT_TASK_PATH.exec(path);
  if (task?.[1] && task[2]) {
    return { name: "projectTask", projectId: task[1].toLowerCase(), taskId: task[2].toLowerCase() };
  }
  const project = PROJECT_PATH.exec(path);
  if (project?.[1]) {
    const projectId = project[1];
    if (!project[2]) {
      // Former links (one release): `?conversation=` opened a thread, `?tab=` a knowledge tab.
      const conversationId = params.get("conversation");
      if (conversationId && new RegExp(`^${UUID}$`, "i").test(conversationId)) {
        return { name: "redirect", to: projectConversationPath(projectId, conversationId) };
      }
      const tab = params.get("tab");
      if (tab === "files" || tab === "wiki") {
        return { name: "redirect", to: projectHomePath(projectId, tab) };
      }
      if (tab !== null) {
        return { name: "redirect", to: projectHomePath(projectId) };
      }
    }
    // The bare URL is the home, on its Overview tab (rendered in place, no redirect).
    const tab = project[2]?.toLowerCase();
    return {
      name: "projectHome",
      projectId,
      tab:
        tab === "conversations" || tab === "files" || tab === "memory" || tab === "tasks"
          ? tab
          : "home",
    };
  }
  return { name: "notFound" };
}

/**
 * The former prefix of every console address (`/console/...`), from before the console served
 * its screens at its origin's root (#390). On the console website a former address redirects
 * to its new one, query included, for at least one release: remove it, with
 * {@link legacyConsoleRedirect}, no earlier than the second release that ships #390 (the
 * follow-up issue names it).
 */
const LEGACY_CONSOLE_PREFIX = "/console";

/** A former `/console/...` address's new one (its query kept), else null. */
function legacyConsoleRedirect(path: string, search: string): string | null {
  if (path !== LEGACY_CONSOLE_PREFIX && !path.startsWith(`${LEGACY_CONSOLE_PREFIX}/`)) {
    return null;
  }
  const rest = path.slice(LEGACY_CONSOLE_PREFIX.length) || "/";
  // `/console//host` or `/console/\host` would read as another origin: the tenant list instead.
  if (/^\/[/\\]/.test(rest)) {
    return "/";
  }
  // `search` is a URL's (empty, or starting with "?").
  return `${rest}${search}`;
}

/**
 * The console website's routes (its own origin, `console.html`): its screens at the root, the
 * first tab of a tenant and of Operators at their bare URLs, and former `/console/...`
 * addresses redirected (#390). Anything else is `notFound` (the console then opens its tenant
 * list).
 */
export function parseConsoleRoute(pathname: string, search = ""): Route {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const legacy = legacyConsoleRedirect(path, search);
  if (legacy !== null) {
    return { name: "redirect", to: legacy };
  }
  if (path === `/operators/${OPERATORS_TABS[0]}`) {
    return { name: "redirect", to: consoleOperatorsPath() };
  }
  const tenantFirstTab = CONSOLE_TENANT_FIRST_TAB_PATH.exec(path);
  if (tenantFirstTab?.[1]) {
    return { name: "redirect", to: consoleTenantPath(tenantFirstTab[1].toLowerCase()) };
  }
  const view = parseConsole(path);
  return view ? { name: "console", view } : { name: "notFound" };
}

function parseConsole(path: string): ConsoleView | null {
  switch (path) {
    case "/":
    case "/tenants":
      return { name: "tenants" };
    case "/plans":
      return { name: "plans" };
    case CONSOLE_MODEL_CONNECTIONS_PATH:
      return { name: "modelConnections" };
    case "/policies":
      return { name: "policies" };
    case "/support":
      return { name: "support" };
    case "/reports":
      return { name: "reports" };
    case "/feedback":
      return { name: "feedback" };
    case "/feedback/groups":
      return { name: "feedbackGroups" };
    case CONSOLE_PROFILE_PATH:
      return { name: "profile" };
    case "/sessions":
      // The former page: operator sessions now sit under Operators.
      return { name: "operators", tab: "sessions" };
  }
  const feedbackGroup = CONSOLE_FEEDBACK_GROUP_PATH.exec(path);
  if (feedbackGroup?.[1]) {
    return { name: "feedbackGroups", groupId: feedbackGroup[1].toLowerCase() };
  }
  const feedback = CONSOLE_FEEDBACK_REPORT_PATH.exec(path);
  if (feedback?.[1]) {
    return { name: "feedback", reportId: feedback[1].toLowerCase() };
  }
  const operators = CONSOLE_OPERATORS_PATH.exec(path);
  if (operators) {
    return { name: "operators", tab: (operators[1] as OperatorsTab | undefined) ?? "sessions" };
  }
  const policies = CONSOLE_POLICIES_GROUP_PATH.exec(path);
  if (policies?.[1]) {
    return { name: "policies", group: policies[1] as SettingsGroup };
  }
  const tenant = CONSOLE_TENANT_PATH.exec(path);
  if (tenant?.[1]) {
    return {
      name: "tenant",
      tenantId: tenant[1].toLowerCase(),
      tab: (tenant[2]?.toLowerCase() as TenantTab | undefined) ?? "overview",
    };
  }
  const grantSettings = CONSOLE_GRANT_SETTINGS_PATH.exec(path);
  if (grantSettings?.[1] && grantSettings[2]) {
    return {
      name: "supportGrant",
      grantId: grantSettings[1].toLowerCase(),
      section: "settings",
      settingsGroup: grantSettings[2].toLowerCase() as SettingsGroup,
    };
  }
  const grantProject = CONSOLE_GRANT_PROJECT_PATH.exec(path);
  if (grantProject?.[1] && grantProject[2]) {
    return {
      name: "supportGrant",
      grantId: grantProject[1].toLowerCase(),
      section: "projects",
      projectId: grantProject[2].toLowerCase(),
    };
  }
  const grant = CONSOLE_GRANT_PATH.exec(path);
  if (grant?.[1] && grant[2] !== "support") {
    return {
      name: "supportGrant",
      grantId: grant[1].toLowerCase(),
      section: (grant[2] as BackofficeSection | undefined) ?? null,
    };
  }
  return null;
}

export function areaOf(route: Route): Area | null {
  switch (route.name) {
    case "home":
      return "home";
    case "projects":
    case "projectTask":
    case "projectHome":
    case "projectConversation":
    case "projectSettings":
    case "wiki":
    case "wikiNew":
    case "wikiEdit":
    case "wikiDraft":
    case "wikiChange":
    case "wikiHistory":
    case "wikiCompare":
      return "projects";
    case "conversation":
      return "conversations";
    case "redirect":
      return null;
    case "conversations":
    case "tasks":
    case "knowledge":
    case "backoffice":
      return route.name;
    case "account":
    case "invitation":
    case "resetPassword":
    case "signIn":
    case "connect":
    case "device":
    case "notFound":
    case "console":
      return null;
  }
}

/**
 * The route's name for client telemetry (openspec change `client-telemetry` D3): its kind and
 * view as lowercase words (`project.files`, `backoffice.settings.security`), never an
 * identifier, a query or a title. Matches the shared `ROUTE_NAME` grammar.
 */
export function routeName(route: Route): string {
  switch (route.name) {
    case "projectHome":
      return `project.${route.tab}`;
    case "projectTask":
      return "project.task";
    case "projectConversation":
      return "project.conversation";
    case "projectSettings":
      return route.group ? `project.settings.${route.group}` : "project.settings";
    case "wiki":
      return `wiki.${route.tab}`;
    case "wikiNew":
      return "wiki.new";
    case "wikiEdit":
      return "wiki.edit";
    case "wikiDraft":
      return "wiki.draft";
    case "wikiChange":
      return "wiki.change";
    case "wikiHistory":
      return "wiki.history";
    case "wikiCompare":
      return "wiki.compare";
    case "backoffice":
      if (route.projectId !== undefined) {
        return "backoffice.projects.project";
      }
      if (route.settingsGroup !== undefined) {
        return `backoffice.settings.${route.settingsGroup}`;
      }
      return route.section ? `backoffice.${route.section}` : "backoffice";
    case "console":
      return "console";
    case "resetPassword":
      return "password.reset";
    case "signIn":
      return "signin";
    case "notFound":
      return "notfound";
    case "account":
      return route.view ? `account.${route.view}` : "account";
    case "home":
    case "projects":
    case "conversation":
    case "redirect":
    case "conversations":
    case "tasks":
    case "knowledge":
    case "invitation":
    case "connect":
    case "device":
      return route.name;
  }
}

export const HOME_PATH = "/";
export const PROJECTS_PATH = "/projects";
export const BACKOFFICE_PATH_ROOT = "/backoffice";
export const USERS_PATH = "/backoffice/users";
export const ENTITIES_PATH = "/backoffice/entities";
export const ADMIN_SETTINGS_PATH = "/backoffice/settings";
/** One settings page, under the backoffice's `base`, at a level (`?level=<entityId>`) if set. */
export const adminSettingsPath = (
  group: SettingsGroup,
  base = BACKOFFICE_PATH_ROOT,
  level: string | null = null,
) => `${base}/settings/${group}${level ? `?${new URLSearchParams({ level }).toString()}` : ""}`;
export const DIRECTORY_PATH = "/backoffice/directory";
export const RESIDENCY_PATH = "/backoffice/residency";
export const MODELS_PATH = "/backoffice/models";
export const REPORTS_PATH = "/backoffice/reports";
export const GOVERNED_PROJECTS_PATH = "/backoffice/projects";
export const AUDIT_PATH = "/backoffice/audit";
export const PLAN_PATH = "/backoffice/plan";
export const SUPPORT_PATH = "/backoffice/support";
/** The console website serves its screens at the root of its own origin (#390). */
export const CONSOLE_PATH = "/";
export const CONSOLE_PLANS_PATH = "/plans";
export const CONSOLE_MODEL_CONNECTIONS_PATH = "/model-connections";
export const CONSOLE_POLICIES_PATH = "/policies";
/** One page of the instance policies. */
export const consolePoliciesPath = (group: SettingsGroup) => `${CONSOLE_POLICIES_PATH}/${group}`;
export const CONSOLE_SUPPORT_PATH = "/support";
export const CONSOLE_REPORTS_PATH = "/reports";
export const CONSOLE_FEEDBACK_PATH = "/feedback";
export const CONSOLE_PROFILE_PATH = "/profile";
/** The Feedback inbox with its filters' query string (feedback-triage-ux D1), if any. */
export const consoleFeedbackInboxPath = (search = "") =>
  search ? `${CONSOLE_FEEDBACK_PATH}?${search}` : CONSOLE_FEEDBACK_PATH;
/**
 * Where a report was opened from (feedback-triage-ux D2), so its breadcrumb leads back there:
 * the inbox with its filters (`inbox`, the inbox's query string) or one group.
 */
export type FeedbackReportOrigin = { inbox: string } | { group: string };
export const consoleFeedbackPath = (reportId: string, from?: FeedbackReportOrigin) => {
  const path = `${CONSOLE_FEEDBACK_PATH}/${encodeURIComponent(reportId)}`;
  // The plain URL is a pasted link: it leads back to the unfiltered inbox and steps nowhere. An
  // unfiltered inbox origin is kept as `?inbox=`, so Previous/Next follow it
  // (feedback-inbox-followups D5).
  if (!from) {
    return path;
  }
  const query = new URLSearchParams(
    "group" in from ? { group: from.group } : { inbox: from.inbox },
  );
  return `${path}?${query.toString()}`;
};
export const CONSOLE_FEEDBACK_GROUPS_PATH = "/feedback/groups";
export const consoleFeedbackGroupPath = (groupId: string) =>
  `${CONSOLE_FEEDBACK_GROUPS_PATH}/${encodeURIComponent(groupId)}`;
/** The Operators area (its Sessions tab at the bare URL). */
export const consoleOperatorsPath = (tab: OperatorsTab = "sessions") =>
  tab === "sessions" ? "/operators" : `/operators/${tab}`;
export const consoleTenantPath = (tenantId: string, tab: TenantTab = "overview") =>
  `/tenants/${encodeURIComponent(tenantId)}${tab === "overview" ? "" : `/${tab}`}`;
/** A tenant's backoffice under a support grant (its index, or one section). */
export const supportGrantPath = (grantId: string, section?: BackofficeSection) =>
  `/support/${encodeURIComponent(grantId)}${section ? `/${section}` : ""}`;
export const REPORT_TABS: readonly ReportTab[] = ["usage", "quality", "cost", "health"];

/** Reports on one tab (Usage needs no parameter), keeping the period and entity filters. */
export const reportsPath = (
  tab: ReportTab,
  filters: { from?: string | undefined; to?: string | undefined; entity?: string | undefined } = {},
  base = BACKOFFICE_PATH_ROOT,
) => {
  const query = new URLSearchParams();
  if (tab !== "usage") {
    query.set("tab", tab);
  }
  for (const key of ["from", "to", "entity"] as const) {
    const value = filters[key];
    if (value) {
      query.set(key, value);
    }
  }
  const qs = query.toString();
  return `${base}/reports${qs ? `?${qs}` : ""}`;
};

/** The audit events of one action (e.g. `project.break_glass`). */
export const auditPath = (action: string, base = BACKOFFICE_PATH_ROOT) =>
  `${base}/audit?action=${encodeURIComponent(action)}`;
export const ACCOUNT_PATH = "/account";
/** The caller's own feedback reports and their status (in-app-feedback). */
export const MY_FEEDBACK_PATH = "/account/feedback";
export const RESET_PASSWORD_PATH = "/reset-password";

/**
 * The email typed at sign-in, handed to the reset form in the history entry (never the URL or
 * storage), so Forgot password keeps it (#403).
 */
export interface ResetHandoff {
  resetEmail: string;
}

export function isResetHandoff(value: unknown): value is ResetHandoff {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { resetEmail?: unknown }).resetEmail === "string"
  );
}

/**
 * A project's home page, which opens on its Overview tab (`home`, the bare URL), or one of its
 * tabs. The Wiki tab's own URL is the wiki's (`/wiki`), where its sub-tabs and pages live.
 */
export const projectHomePath = (projectId: string, tab?: ProjectTab) =>
  `/projects/${encodeURIComponent(projectId)}${tab && tab !== "home" ? `/${tab}` : ""}`;

/** A conversation of a project (`"new"` starts one). */
export const projectConversationPath = (projectId: string, conversationId: string) =>
  `/projects/${encodeURIComponent(projectId)}/c/${encodeURIComponent(conversationId)}`;

/** A conversation at its own URL: its project's, or `/c/:id` outside any project. */
export const conversationPath = (conversation: { id: string; projectId: string | null }) =>
  conversation.projectId
    ? projectConversationPath(conversation.projectId, conversation.id)
    : `/c/${encodeURIComponent(conversation.id)}`;

export const CONVERSATIONS_PATH = "/conversations";

/** A project's settings, optionally on one group (without one, General opens in place). */
export const projectSettingsPath = (projectId: string, group?: ProjectSettingsGroup) =>
  `/projects/${encodeURIComponent(projectId)}/settings${group ? `/${group}` : ""}`;

/** The project ID of a route inside one project, else null. */
export function projectOf(route: Route): string | null {
  switch (route.name) {
    case "projectTask":
    case "projectHome":
    case "projectConversation":
    case "projectSettings":
    case "wiki":
    case "wikiNew":
    case "wikiEdit":
    case "wikiDraft":
    case "wikiChange":
    case "wikiHistory":
    case "wikiCompare":
      return route.projectId;
    default:
      return null;
  }
}

/** The conversation a route shows, else null. */
export function conversationOf(route: Route): string | null {
  return route.name === "conversation" ||
    (route.name === "projectConversation" && route.conversationId !== "new")
    ? route.conversationId
    : null;
}

/** The wiki of a project, or one of its pages (a cited version with `?version=`). */
export const wikiPath = (projectId: string, pageId?: string, version?: number) =>
  `/projects/${encodeURIComponent(projectId)}/wiki${pageId ? `/${encodeURIComponent(pageId)}` : ""}${
    version ? `?version=${version}` : ""
  }`;

/** The wiki on one of its tabs (Pages needs no parameter). */
export const wikiTabPath = (projectId: string, tab: WikiTab) =>
  `/projects/${encodeURIComponent(projectId)}/wiki${tab === "pages" ? "" : `?tab=${tab}`}`;

/** The Changes inbox on one view (To decide needs no parameter). */
export const wikiChangesPath = (projectId: string, view: InboxView = "to-decide") =>
  `${wikiTabPath(projectId, "changes")}${view === "to-decide" ? "" : `&view=${view}`}`;

/** The Wiki tab's pages that need checking (stale or unverified), with their reason. */
export const wikiNeedsCheckingPath = (projectId: string) =>
  `/projects/${encodeURIComponent(projectId)}/wiki?filter=needs-checking`;

/** The page editor of a new page, in `folderId` when given. */
export const newPagePath = (projectId: string, folderId?: string | null) =>
  `/projects/${encodeURIComponent(projectId)}/wiki/new${
    folderId ? `?folder=${encodeURIComponent(folderId)}` : ""
  }`;

/** The page editor of a proposed change to `pageId`. */
export const editPagePath = (projectId: string, pageId: string) =>
  `/projects/${encodeURIComponent(projectId)}/wiki/${encodeURIComponent(pageId)}/edit`;

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

/**
 * Goes to `path`. `state` travels in the history entry only (never the URL or storage): home
 * hands its question to the project this way.
 */
export function navigate(path: string, options: { state?: unknown; replace?: boolean } = {}): void {
  for (const guard of navigationGuards) {
    if (!guard(() => navigate(path, options))) {
      return;
    }
  }
  if (options.replace) {
    window.history.replaceState(options.state ?? null, "", path);
  } else if (
    path !== window.location.pathname + window.location.search ||
    options.state !== undefined
  ) {
    window.history.pushState(options.state ?? null, "", path);
  }
  for (const listener of listeners) {
    listener();
  }
  // A route change may be a safe point for a pending update, at the new address.
  onRouteChange();
}

const location = () => window.location.pathname + window.location.search;

export function useRoute(): Route {
  const href = useSyncExternalStore(subscribe, location);
  const url = new URL(href, "https://esi.invalid");
  return parseRoute(url.pathname, url.search);
}

/** Which site's routes this bundle parses: the console's entry sets it once (#390). */
let site: "app" | "console" = "app";
export function setRouterSite(value: "app" | "console"): void {
  site = value;
}

/** The current location's route name for telemetry and feedback, on this bundle's site. */
export function currentRouteName(): string {
  const parse = site === "console" ? parseConsoleRoute : parseRoute;
  return routeName(parse(window.location.pathname, window.location.search));
}

/** The console website's {@link useRoute} (its routes at the root, #390). */
export function useConsoleRoute(): Route {
  const href = useSyncExternalStore(subscribe, location);
  const url = new URL(href, "https://esi.invalid");
  return parseConsoleRoute(url.pathname, url.search);
}

/** The current query string (e.g. a wiki page's `?version=`). */
export function useSearch(): URLSearchParams {
  return new URLSearchParams(useSyncExternalStore(subscribe, () => window.location.search));
}

/**
 * The state of the current history entry, read without side effects; updates on every in-app
 * navigation (a new entry's state) and on back/forward.
 */
export function useHistoryState(): unknown {
  return useSyncExternalStore(subscribe, () => window.history.state as unknown);
}

/**
 * Takes the state handed with the current history entry, once: the entry is replaced without
 * it, so a reload or coming back never acts on it again.
 */
export function takeHistoryState<T>(guard: (value: unknown) => value is T): T | null {
  const state: unknown = window.history.state;
  if (!guard(state)) {
    return null;
  }
  window.history.replaceState(null, "", location());
  return state;
}

/**
 * An in-app link: a real `<a>` (open in new tab works), navigated without a reload. `state`
 * travels in the history entry of an in-app navigation only (see `navigate`).
 */
export function Link({
  href,
  onClick,
  state,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { state?: unknown }) {
  return (
    <a
      {...props}
      href={href}
      onClick={(e) => {
        onClick?.(e);
        if (
          !href ||
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey ||
          props.target === "_blank"
        ) {
          return;
        }
        e.preventDefault();
        navigate(href, { state });
      }}
    />
  );
}

/** The artefact open beside a conversation (`?artefact=<id>&v=<n>`, artefacts-viewer). */
export interface OpenArtefact {
  artefactId: string;
  /** The version shown; null: the newest the viewer may read. */
  version: number | null;
}

/** Reads `?artefact=&v=` (IDs and version numbers only, never content). */
export function openArtefactOf(search: string | URLSearchParams): OpenArtefact | null {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const artefactId = params.get("artefact");
  if (!artefactId || !new RegExp(`^${UUID}$`, "i").test(artefactId)) {
    return null;
  }
  const v = params.get("v");
  const version = v !== null && /^[1-9][0-9]{0,5}$/.test(v) ? Number(v) : null;
  return { artefactId: artefactId.toLowerCase(), version };
}

/** The current URL with `artefact` and `v` set to `open`, or removed (null). */
export function withOpenArtefact(href: string, open: OpenArtefact | null): string {
  const url = new URL(href, "https://esi.invalid");
  url.searchParams.delete("artefact");
  url.searchParams.delete("v");
  if (open) {
    // One occupant of the right column (web-shell): opening an artefact closes the wiki panel.
    for (const key of WIKI_PANEL_PARAMS) {
      url.searchParams.delete(key);
    }
    url.searchParams.set("artefact", open.artefactId);
    if (open.version !== null) {
      url.searchParams.set("v", String(open.version));
    }
  }
  return url.pathname + url.search + url.hash;
}

/**
 * What the conversation's wiki side panel shows (wiki-collaboration D8a): a page (`?wiki=` and
 * `wv` for a version), or a change set (`?changes=` and `item`).
 */
export type OpenWiki =
  | { kind: "page"; pageId: string; version: number | null }
  | {
      kind: "changes";
      changeSetId: string;
      itemId: string | null;
      /** A resolution run's replies to validate (wiki-resolve-with-agent D4): `?run=`. */
      runId?: string | null;
    };
const WIKI_PANEL_PARAMS = ["wiki", "wv", "changes", "item", "run"] as const;

export function openWikiOf(search: string | URLSearchParams): OpenWiki | null {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const id = (key: string) => {
    const value = params.get(key);
    return value && UUID_VALUE.test(value) ? value.toLowerCase() : null;
  };
  const pageId = id("wiki");
  if (pageId) {
    const v = params.get("wv");
    return {
      kind: "page",
      pageId,
      version: v !== null && /^[1-9][0-9]{0,5}$/.test(v) ? Number(v) : null,
    };
  }
  const changeSetId = id("changes");
  if (!changeSetId) {
    return null;
  }
  const runId = id("run");
  return { kind: "changes", changeSetId, itemId: id("item"), ...(runId && { runId }) };
}

/** The current URL with the wiki panel's target set to `open`, or removed (null). */
export function withOpenWiki(href: string, open: OpenWiki | null): string {
  const url = new URL(href, "https://esi.invalid");
  for (const key of WIKI_PANEL_PARAMS) {
    url.searchParams.delete(key);
  }
  if (open) {
    url.searchParams.delete("artefact");
    url.searchParams.delete("v");
    if (open.kind === "page") {
      url.searchParams.set("wiki", open.pageId);
      if (open.version !== null) {
        url.searchParams.set("wv", String(open.version));
      }
    } else {
      url.searchParams.set("changes", open.changeSetId);
      if (open.itemId) {
        url.searchParams.set("item", open.itemId);
      }
      if (open.runId) {
        url.searchParams.set("run", open.runId);
      }
    }
  }
  return url.pathname + url.search + url.hash;
}

const showWiki = (next: OpenWiki, options: { replace?: boolean } = {}) =>
  goToArtefactUrl(withOpenWiki(location() + window.location.hash, next), options);
const closeWiki = () => goToArtefactUrl(withOpenWiki(location() + window.location.hash, null));

/** The wiki panel's target and how to change it; opening pushes a history entry. */
export function useOpenWiki() {
  const open = openWikiOf(useSearch());
  return { open, show: showWiki, close: closeWiki };
}

/**
 * The open artefact and how to change it. Opening and closing push history entries, so the back
 * button closes the panel; switching versions replaces the entry.
 */
export function useOpenArtefact() {
  const open = openArtefactOf(useSearch());
  return { open, show: showArtefact, close: closeArtefact };
}

/** Goes to `path` unless it is the current URL (fragment included): no duplicate entries. */
function goToArtefactUrl(path: string, options: { replace?: boolean } = {}) {
  if (path === location() + window.location.hash) {
    return;
  }
  // A replaced entry keeps the state it carried.
  navigate(path, { ...options, ...(options.replace ? { state: window.history.state } : {}) });
}

// Module-level, so callers get stable functions. The URL's fragment goes along.
const showArtefact = (next: OpenArtefact, options: { replace?: boolean } = {}) =>
  goToArtefactUrl(withOpenArtefact(location() + window.location.hash, next), options);
const closeArtefact = () =>
  goToArtefactUrl(withOpenArtefact(location() + window.location.hash, null));

export const changeSetPath = (projectId: string, id: string, itemId?: string) =>
  `/projects/${encodeURIComponent(projectId)}/wiki/changes/${encodeURIComponent(id)}${itemId ? `?item=${encodeURIComponent(itemId)}` : ""}`;
/**
 * A Changed agent reply's "See the change" (wiki-resolve-with-agent D7, amended): the thread's
 * page in the review at its passage, with "What changed since my last review" on when the
 * viewer has marks (`since=1`), else Marked against the base.
 */
export const seeChangePath = (
  projectId: string,
  setId: string,
  itemId: string | null,
  threadId: string,
) => `${threadInReviewPath(projectId, setId, itemId, threadId)}&since=1`;
/** A thread in its change set's review, at its passage. */
export const threadInReviewPath = (
  projectId: string,
  setId: string,
  itemId: string | null,
  threadId: string,
) => {
  const path = changeSetPath(projectId, setId, itemId ?? undefined);
  return `${path}${path.includes("?") ? "&" : "?"}thread=${encodeURIComponent(threadId)}`;
};
export const pageHistoryPath = (projectId: string, pageId: string) =>
  `/projects/${encodeURIComponent(projectId)}/wiki/${encodeURIComponent(pageId)}/history`;
/** Two versions of a page compared, oldest first whatever the order given. */
export const pageComparePath = (projectId: string, pageId: string, a: number, b: number) =>
  `/projects/${encodeURIComponent(projectId)}/wiki/${encodeURIComponent(pageId)}/compare?from=${Math.min(a, b)}&to=${Math.max(a, b)}`;

const navigationGuards = new Set<(proceed: () => void) => boolean>();
export function registerNavigationGuard(guard: (proceed: () => void) => boolean) {
  navigationGuards.add(guard);
  return () => {
    navigationGuards.delete(guard);
  };
}

export const projectTaskPath = (projectId: string, taskId: string) =>
  `${projectHomePath(projectId, "tasks")}/${encodeURIComponent(taskId)}`;
